import {
  Box,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { motion, Variants } from "framer-motion";
import QuizIcon from "@mui/icons-material/Quiz";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import { useQuestionsContext } from "../../../context/QuestionsContext";
import PageMeta from '../../../components/PageMeta';

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.4,
      ease: "easeOut" as const,
    },
  }),
};

const difficultyColors = {
  easy: "success",
  medium: "info",
  hard: "warning",
  veryHard: "secondary",
  impossible: "error",
} as const;

const difficultyLabels = {
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
  veryHard: "Very Hard",
  impossible: "Impossible",
};

const BOOK_ORDER = [
  "Harry Potter and the Philosopher's Stone",
  "Harry Potter and the Chamber of Secrets",
  "Harry Potter and the Prisoner of Azkaban",
  "Harry Potter and the Goblet of Fire",
  "Harry Potter and the Order of the Phoenix",
  "Harry Potter and the Half-Blood Prince",
  "Harry Potter and the Deathly Hallows",
];

const COMING_SOON_SOURCES = [
  { title: "Fantastic Beasts and Where to Find Them", type: "Movie" },
  { title: "Hogwarts Legacy", type: "Game" },
];

const getSortIndex = (title: string) => {
  const index = BOOK_ORDER.indexOf(title);
  return index === -1 ? BOOK_ORDER.length : index;
};

export default function QuestionBreakdownPage() {
  const { difficultyStats, sourceStats, totalQuestions, questions } =
    useQuestionsContext();

  return (
    <>
      <PageMeta
        title="Question Breakdown | The Quizzler"
        description="Explore how The Quizzler's Harry Potter trivia questions are distributed across books, films, difficulty levels, and categories."
        canonical="https://thequizzler.app/question-breakdown"
      />
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant="h3"
            fontWeight={700}
            gutterBottom
            sx={{
              letterSpacing: "-0.5px",
              fontSize: { xs: "2rem", md: "3rem" },
            }}
          >
            Trivia Question Breakdown
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              mb: 4,
              maxWidth: 700,
            }}
          >
            Explore the growing question library across books,
            films, games, and more. New questions are added regularly as
            The Quizzler continues to grow.
          </Typography>

          <Divider sx={{ mb: 4 }} />
        </motion.div>

        {/* Overview Panel */}
        <motion.div
          custom={0}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
        >
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2.5, md: 3.5 },
              borderRadius: 3,
              mb: 5,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
              <QuizIcon color="primary" />
              <Typography variant="h5" fontWeight={700}>
                Question Difficulty Overview
              </Typography>
            </Stack>

            <Typography
              variant="h3"
              fontWeight={700}
              sx={{
                mb: 0.5,
                fontSize: { xs: "2rem", md: "2.5rem" },
              }}
            >
              {totalQuestions.toLocaleString()}
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Total Questions
            </Typography>

            <Stack spacing={2}>
              {Object.entries(difficultyLabels).map(([key, label], index) => {
                const count =
                  difficultyStats[key as keyof typeof difficultyStats];

                const percentage = Math.round(
                  (count / totalQuestions) * 100
                );

                return (
                  <Box key={key}>
                    {index > 0 && <Divider sx={{ mb: 2 }} />}

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        flexWrap: "wrap",
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Chip
                          label={label}
                          color={
                            difficultyColors[
                              key as keyof typeof difficultyColors
                            ]
                          }
                          size="small"
                        />

                        <Typography variant="body1" color="text.secondary">
                          {count.toLocaleString()} questions
                        </Typography>
                      </Stack>

                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="text.secondary"
                      >
                        {percentage}% of total
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        </motion.div>

        {/* Source Breakdown */}
        <motion.div
          custom={1}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
            <AutoStoriesIcon color="primary" />
            <Typography variant="h5" fontWeight={700}>
              Questions by Source
            </Typography>
          </Stack>
        </motion.div>

        <Grid container spacing={3}>
          {[...sourceStats]
              .sort((a, b) => getSortIndex(a.title) - getSortIndex(b.title))
              .map((source, i) => {
                  const sourcePercentage = Math.round(
                      (source.total / totalQuestions) * 100
                  );

                  return (
                      <Grid size={{ xs: 12, sm: 6 }} key={source.sourceId}>
                      <motion.div
                          custom={i + 2}
                          initial="hidden"
                          animate="visible"
                          variants={fadeUp}
                          style={{ height: "100%" }}
                      >
                          <Paper
                          variant="outlined"
                          sx={{
                              p: 3,
                              borderRadius: 3,
                              height: "100%",
                              transition: "transform 0.2s ease, box-shadow 0.2s ease",
                              "&:hover": {
                              transform: "translateY(-3px)",
                              boxShadow: 3,
                              },
                          }}
                          >
                          <Typography variant="h6" fontWeight={700} gutterBottom>
                              {source.title}{source.type === 'Movie' ? ' (Movie)' : ''}
                          </Typography>

                          <Typography variant="body1" sx={{ mb: 2 }}>
                              <strong>{source.total.toLocaleString()}</strong> Questions ·{" "}
                              <Typography component="span" color="text.secondary">
                              {sourcePercentage}% of database
                              </Typography>
                          </Typography>

                          <Divider sx={{ mb: 2 }} />

                          <Stack spacing={1.5}>
                              {Object.entries(source.difficulties).map(([key, count]) => (
                              <Box
                                  key={key}
                                  sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: 2,
                                  }}
                              >
                                  <Chip
                                  label={
                                      difficultyLabels[
                                      key as keyof typeof difficultyLabels
                                      ]
                                  }
                                  color={
                                      difficultyColors[
                                      key as keyof typeof difficultyColors
                                      ]
                                  }
                                  size="small"
                                  />

                                  <Typography variant="body2" fontWeight={600}>
                                  {count.toLocaleString()}
                                  </Typography>
                              </Box>
                              ))}
                          </Stack>
                          </Paper>
                      </motion.div>
                      </Grid>
                  );
              })}
              {COMING_SOON_SOURCES.map((source) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={source.title}>
                      <Paper
                      variant="outlined"
                      sx={{
                          p: 3,
                          borderRadius: 3,
                          height: "100%",
                          opacity: 0.6,
                      }}
                      >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="h6" fontWeight={700}>
                          {source.title}{source.type === 'Movie' ? ' (Movie)' : ''}
                          </Typography>
                      </Stack>

                      <Chip label="Coming Soon" size="small" sx={{ mt: 0.5 }} />
                      </Paper>
                  </Grid>
              ))}
        </Grid>
      </Container>
    </>
  );
}